import { Dispatch, SetStateAction } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { NO_LABEL, NO_TEXT_LABEL, OTHER_LABEL, YES_LABEL } from "config/LabelMapping";
import { MAX_CHOICE_BUTTONS } from "features/answerPanel/components/MultichoiceImageActions";
import { DetectorModes } from "types/createDetector";
import { CanvasAnnotation } from "types/imageAnnotations";
import { ImageQuery } from "types/imageQueries";

export interface UseQueryDetailsHotkeysArgs {
  selectedRowIndex: number;
  rows: ImageQuery[];
  rowCount: number;
  pageSize: number;
  page: number;
  handleChangePage: (_: any, newPage: number, targetIndex?: number) => void;
  updateSelectedRowIndex: (index: number) => void;
}

// Base interface for common hotkey arguments
interface BaseUseReviewPageHotkeysArgs {
  detectorMode: DetectorModes;
  isUnclearMenuOpen: boolean;
  setUnclearMenuOpen: (isOpen: boolean) => void;
  hasImageQuery: boolean;
  onLabelSubmit: (label: string) => void;
}

// Specific interfaces for each detector mode
interface CountUseHotkeysArgs extends Omit<BaseUseReviewPageHotkeysArgs, "onLabelSubmit"> {
  annotations?: CanvasAnnotation[];
  valueError?: string;
  setValueError?: Dispatch<SetStateAction<string>>;
  onLabelSubmit: (value: string, annotations?: CanvasAnnotation[]) => void;
  isCountMode?: boolean;
  maxCount?: number;
}

interface MultichoiceUseHotkeysArgs extends BaseUseReviewPageHotkeysArgs {
  choices: string[];
}

interface TextRecognitionUseHotkeysArgs extends BaseUseReviewPageHotkeysArgs {
  inputValue: string | undefined;
  valueError?: string;
  setValueError?: Dispatch<SetStateAction<string>>;
}

// Return type interfaces for each detector mode
interface BinaryHotkeysReturn {
  useYesHotkey: () => ReturnType<typeof useHotkeys>;
  useNoHotkey: () => ReturnType<typeof useHotkeys>;
  useUnclearHotkey: () => ReturnType<typeof useHotkeys>;
}

interface CountHotkeysReturn {
  useSubmitCountHotkey: () => ReturnType<typeof useHotkeys>;
  useUnclearHotkey: () => ReturnType<typeof useHotkeys>;
  useCloseUnclearHotkey: () => ReturnType<typeof useHotkeys>;
}

interface MultichoiceHotkeysReturn {
  useMultichoiceHotkeys: () => ReturnType<typeof useHotkeys>;
  useOtherHotkey: () => ReturnType<typeof useHotkeys>;
  useUnclearHotkey: () => ReturnType<typeof useHotkeys>;
}

interface TextRecognitionHotkeysReturn {
  useTextRecognitionHotkeys: () => ReturnType<typeof useHotkeys>;
  useUnclearHotkey: () => ReturnType<typeof useHotkeys>;
}

// Function overloads for better type inference
// eslint-disable-next-line no-redeclare
export function useReviewPageHotkeys(
  args: BaseUseReviewPageHotkeysArgs & { detectorMode: DetectorModes.BINARY }
): BinaryHotkeysReturn;
// eslint-disable-next-line no-redeclare
export function useReviewPageHotkeys(
  args: CountUseHotkeysArgs & { detectorMode: DetectorModes.COUNT }
): CountHotkeysReturn;
// eslint-disable-next-line no-redeclare
export function useReviewPageHotkeys(
  args: MultichoiceUseHotkeysArgs & { detectorMode: DetectorModes.MULTICHOICE }
): MultichoiceHotkeysReturn;
// eslint-disable-next-line no-redeclare
export function useReviewPageHotkeys(
  args: TextRecognitionUseHotkeysArgs & { detectorMode: DetectorModes.TEXT_RECOGNITION }
): TextRecognitionHotkeysReturn;
// eslint-disable-next-line no-redeclare
export function useReviewPageHotkeys(
  args: BaseUseReviewPageHotkeysArgs | CountUseHotkeysArgs | MultichoiceUseHotkeysArgs | TextRecognitionUseHotkeysArgs
): BinaryHotkeysReturn | CountHotkeysReturn | MultichoiceHotkeysReturn | TextRecognitionHotkeysReturn {
  const { detectorMode } = args;

  // Create hotkeys based on detector mode
  switch (detectorMode) {
    case DetectorModes.BINARY: {
      const { isUnclearMenuOpen, hasImageQuery, setUnclearMenuOpen, onLabelSubmit } =
        args as BaseUseReviewPageHotkeysArgs;
      return createBinaryHotkeys(isUnclearMenuOpen, hasImageQuery, setUnclearMenuOpen, onLabelSubmit);
    }

    case DetectorModes.COUNT: {
      const {
        isUnclearMenuOpen,
        hasImageQuery,
        setUnclearMenuOpen,
        onLabelSubmit,
        annotations,
        valueError,
        setValueError,
        isCountMode,
        maxCount,
      } = args as CountUseHotkeysArgs;
      return createCountHotkeys(
        isUnclearMenuOpen,
        hasImageQuery,
        setUnclearMenuOpen,
        onLabelSubmit,
        annotations,
        valueError,
        setValueError,
        isCountMode,
        maxCount
      );
    }

    case DetectorModes.MULTICHOICE: {
      const { isUnclearMenuOpen, hasImageQuery, setUnclearMenuOpen, onLabelSubmit, choices } =
        args as MultichoiceUseHotkeysArgs;
      return createMultichoiceHotkeys(isUnclearMenuOpen, hasImageQuery, setUnclearMenuOpen, onLabelSubmit, choices);
    }

    case DetectorModes.TEXT_RECOGNITION: {
      const {
        isUnclearMenuOpen,
        hasImageQuery,
        setUnclearMenuOpen,
        onLabelSubmit,
        inputValue,
        valueError,
        setValueError,
      } = args as TextRecognitionUseHotkeysArgs;
      return createTextRecognitionHotkeys(
        isUnclearMenuOpen,
        hasImageQuery,
        setUnclearMenuOpen,
        onLabelSubmit,
        inputValue,
        valueError,
        setValueError
      );
    }

    default:
      throw new Error(`Unsupported detector mode: ${detectorMode}`);
  }
}

const createCommonHotkeyOptions = (isUnclearMenuOpen: boolean, hasImageQuery: boolean) => ({
  enabled: !isUnclearMenuOpen && hasImageQuery,
  enableOnFormTags: ["INPUT"] as const,
  scopes: ["review"],
});

// Reusable hotkey hooks
const createCommonHotkeys = (
  isUnclearMenuOpen: boolean,
  hasImageQuery: boolean,
  setUnclearMenuOpen: (isOpen: boolean) => void
) => {
  const useUnclearHotkey = () =>
    useHotkeys(
      "U",
      () => setUnclearMenuOpen(!isUnclearMenuOpen),
      { enabled: !isUnclearMenuOpen && hasImageQuery, enableOnFormTags: ["INPUT"] as const },
      [hasImageQuery, isUnclearMenuOpen]
    );

  const useCloseUnclearHotkey = () =>
    useHotkeys("esc", () => setUnclearMenuOpen(false), createCommonHotkeyOptions(!isUnclearMenuOpen, hasImageQuery), [
      isUnclearMenuOpen,
      hasImageQuery,
    ]);

  return { useUnclearHotkey, useCloseUnclearHotkey };
};

// Binary hotkeys
const createBinaryHotkeys = (
  isUnclearMenuOpen: boolean,
  hasImageQuery: boolean,
  setUnclearMenuOpen: (isOpen: boolean) => void,
  onLabelSubmit: (label: string) => void
) => {
  const { useUnclearHotkey } = createCommonHotkeys(isUnclearMenuOpen, hasImageQuery, setUnclearMenuOpen);

  const useYesHotkey = () =>
    useHotkeys("1", () => onLabelSubmit(YES_LABEL), createCommonHotkeyOptions(isUnclearMenuOpen, hasImageQuery), [
      isUnclearMenuOpen,
      hasImageQuery,
    ]);

  const useNoHotkey = () =>
    useHotkeys("2", () => onLabelSubmit(NO_LABEL), createCommonHotkeyOptions(isUnclearMenuOpen, hasImageQuery), [
      isUnclearMenuOpen,
      hasImageQuery,
    ]);

  return { useYesHotkey, useNoHotkey, useUnclearHotkey };
};

// Count hotkeys
const createCountHotkeys = (
  isUnclearMenuOpen: boolean,
  hasImageQuery: boolean,
  setUnclearMenuOpen: (isOpen: boolean) => void,
  onLabelSubmit: (value: string, annotations?: CanvasAnnotation[]) => void,
  annotations?: CanvasAnnotation[],
  valueError?: string,
  setValueError?: Dispatch<SetStateAction<string>>,
  isCountMode?: boolean,
  maxCount?: number
) => {
  const { useUnclearHotkey, useCloseUnclearHotkey } = createCommonHotkeys(
    isUnclearMenuOpen,
    hasImageQuery,
    setUnclearMenuOpen
  );

  const value = annotations?.length ?? 0;

  const handleCountSubmit = () => {
    if (value === undefined && setValueError) {
      setValueError("The count value is required");
      return;
    }

    if (valueError) return;
    if (isCountMode) {
      onLabelSubmit(value.toString(), annotations);
    } else {
      let bboxesValue = "NO_OBJECTS";
      if (value > 0) {
        bboxesValue = "BOUNDING_BOX";
      }
      if (value > (maxCount ?? 0)) {
        bboxesValue = "GREATER_THAN_MAX";
      }
      onLabelSubmit(bboxesValue, annotations);
    }
  };

  const useSubmitCountHotkey = () =>
    useHotkeys("ENTER", handleCountSubmit, createCommonHotkeyOptions(isUnclearMenuOpen, hasImageQuery), [
      isUnclearMenuOpen,
      hasImageQuery,
      value,
    ]);

  return { useSubmitCountHotkey, useUnclearHotkey, useCloseUnclearHotkey };
};

// Multichoice hotkeys
const createMultichoiceHotkeys = (
  isUnclearMenuOpen: boolean,
  hasImageQuery: boolean,
  setUnclearMenuOpen: (isOpen: boolean) => void,
  onLabelSubmit: (label: string) => void,
  choices: string[]
) => {
  const { useUnclearHotkey } = createCommonHotkeys(isUnclearMenuOpen, hasImageQuery, setUnclearMenuOpen);

  const handleOtherSubmit = () => {
    onLabelSubmit((choices.length - 1).toString());
  };

  const useMultichoiceHotkeys = () =>
    useHotkeys(
      choices.map((_, index) => (index + 1).toString()).join(","),
      (e) => {
        const choiceIndex = parseInt(e.key, 10) - 1;
        onLabelSubmit(choiceIndex.toString());
      },
      {
        enabled: choices.length > 0 && choices.length <= MAX_CHOICE_BUTTONS && !isUnclearMenuOpen && hasImageQuery,
        enableOnFormTags: ["INPUT"],
        scopes: ["review"],
      },
      [choices, hasImageQuery]
    );

  const useOtherHotkey = () =>
    useHotkeys(
      "O,0",
      handleOtherSubmit,
      {
        enabled: choices.length > 0 && choices.includes(OTHER_LABEL) && !isUnclearMenuOpen && hasImageQuery,
        enableOnFormTags: ["INPUT"],
        scopes: ["review"],
      },
      [choices, hasImageQuery]
    );
  return { useMultichoiceHotkeys, useOtherHotkey, useUnclearHotkey };
};

// Text recognition hotkeys
const createTextRecognitionHotkeys = (
  isUnclearMenuOpen: boolean,
  hasImageQuery: boolean,
  setUnclearMenuOpen: (isOpen: boolean) => void,
  onLabelSubmit: (label: string) => void,
  inputValue: string | undefined,
  valueError?: string,
  setValueError?: Dispatch<SetStateAction<string>>
) => {
  const { useUnclearHotkey } = createCommonHotkeys(isUnclearMenuOpen, hasImageQuery, setUnclearMenuOpen);

  const handleSubmit = () => {
    if (inputValue !== NO_TEXT_LABEL && !inputValue && setValueError) {
      setValueError("The text string value is required");
      return;
    }

    if (valueError) return;

    onLabelSubmit(inputValue ?? "");
  };

  const useTextRecognitionHotkeys = () =>
    useHotkeys("ENTER", handleSubmit, createCommonHotkeyOptions(isUnclearMenuOpen, hasImageQuery), [
      isUnclearMenuOpen,
      hasImageQuery,
    ]);

  return { useTextRecognitionHotkeys, useUnclearHotkey };
};

export const createUndoHotkey = (canUndo: boolean, isLabeling: boolean, undoLabel: () => void) => {
  const useUndoHotkey = () =>
    useHotkeys(
      "esc",
      undoLabel,
      { enabled: canUndo && isLabeling, enableOnFormTags: ["INPUT"] as const, scopes: ["review"] },
      [canUndo, isLabeling]
    );

  return { useUndoHotkey };
};

export function useQueryDetailsHotkeys(args: UseQueryDetailsHotkeysArgs) {
  const { selectedRowIndex, rows, rowCount, pageSize, page, handleChangePage, updateSelectedRowIndex } = args;

  const useNextImageQueryHotkey = () => {
    const onNextImageQuery = () => {
      const nextIndex = selectedRowIndex + 1;

      // If we can move to next item in current page
      if (nextIndex < rows.length) {
        updateSelectedRowIndex(nextIndex);
        return;
      }

      // If we're at the last item of the page and there are more pages available
      const totalPages = Math.ceil(rowCount / pageSize);
      if (selectedRowIndex === rows.length - 1 && page < totalPages) {
        handleChangePage(null, page, 0);
      }
    };

    useHotkeys("down", () => {
      onNextImageQuery();
    });
  };

  const usePreviousImageQueryHotkey = () => {
    const onPreviousImageQuery = () => {
      const previousIndex = selectedRowIndex - 1;

      // If we can move to previous item in current page
      if (previousIndex >= 0) {
        updateSelectedRowIndex(previousIndex);
        return;
      }

      // If we're at the first item of the page and there are previous pages available
      if (selectedRowIndex === 0 && page > 1) {
        handleChangePage(null, page - 2, pageSize - 1);
      }
    };
    useHotkeys("up", () => {
      onPreviousImageQuery();
    });
  };

  return {
    useNextImageQueryHotkey,
    usePreviousImageQueryHotkey,
  };
}
